import React, { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { logoutUser, selectUserInfo } from "../auth/login/loginSlice";
import { useGetUserDataQuery } from "../auth/dataAccess";
import Menu from "../menu/Menu";
import DisplayText from "../../components/DisplayText/DisplayText";
import LogoImg from "../../assets/SquareLogo.png";
import ComingSoonFeature from "../../components/ComingSoonFeature/ComingSoonFeature";
import {
    Drawer,
    AppBar,
    Toolbar,
    IconButton,
    Badge,
    Menu as AccountMenu,
    MenuItem,
    Button,
    Divider,
    Typography,
} from "@mui/material";
import {
    AccountCircleOutlined,
    Menu as MenuIcon,
    NotificationsNoneOutlined,
    PersonOutline,
    ExitToApp,
} from "@mui/icons-material";
import { Box, styled } from "@mui/system";
import theme from "../../app/theme";
import TrialDropdown from "./TrialDropdown";
import ExportCustomFormModal from "../menu/ExportFormModal";
import RolesDropdown from "./SwitchRole";
import useRoles from "../permissions/useRoles";
import { useGetRolesQuery } from "../upload/dataAccess";
const Image = styled("img")(({ theme }) => ({
    maxHeight: theme.spacing(40),
    display: "none",
    [theme.breakpoints.up("sm")]: {
        display: "block",
    },
}));

const NavBar = () => {
    const location = useLocation();
    const { data: user } = useGetUserDataQuery();
    const { data: userRoles } = useGetRolesQuery(user?.userID ?? "");
    const { userActiveRole, getRoleLabel } = useRoles();
    const [profileAnchorEl, setProfileAnchorEl] = useState<null | HTMLElement>(null);
    const isProfileMenuOpen = Boolean(profileAnchorEl);
    const dispatch = useAppDispatch();

    const [menuOpen, setOpen] = useState(false);
    const [exportModalOpen, setExportModalOpen] = React.useState(false);

    const handleExportClick = () => {
        setExportModalOpen(true);
    };

    const handleClose = () => {
        setExportModalOpen(false);
    };

    const { data: userData } = useGetUserDataQuery();

    const handleLogOut = () => {
        dispatch(logoutUser());
    };

    const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setProfileAnchorEl(event.currentTarget);
    };

    const handleProfileMenuClose = () => {
        setProfileAnchorEl(null);
    };

    const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
        if (
            event.type === "keydown" &&
            ((event as React.KeyboardEvent).key === "Tab" || (event as React.KeyboardEvent).key === "Shift")
        ) {
            return;
        }
        setOpen(open);
    };

    const menuId = "primary-search-account-menu";

    const profileMenu = useMemo(
        () => (
            <AccountMenu
                anchorEl={profileAnchorEl}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                id={menuId}
                keepMounted
                open={isProfileMenuOpen}
                onClose={handleProfileMenuClose}
                sx={{ padding: 0 }}
                PaperProps={{
                    sx: {
                        width: "250px",
                        margin: 0,
                        padding: 0,
                        borderRadius: "8px",
                    },
                }}
            >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, p: "4px 12px 12px 12px" }}>
                    <DisplayText type="bodyMedium" variant="semiBold" text={userData?.fullName || ""} />
                    <DisplayText
                        style={{ color: "#9aa7c6" }}
                        type="bodyXSmall"
                        variant="regular"
                        text={userData?.email || ""}
                    />
                </Box>
                {userRoles?.roles?.length > 1 ? <Divider variant="fullWidth" /> : null}
                {userRoles?.roles?.length > 1 ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, p: 12 }}>
                        <DisplayText type="bodyMedium" variant="semiBold" text="Current Role" />
                        <RolesDropdown />
                    </Box>
                ) : null}

                <Divider variant="fullWidth" />

                <MenuItem
                    onClick={handleProfileMenuClose}
                    sx={{
                        p: "8px 12px",
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                    }}
                >
                    <PersonOutline fontSize="small" />
                    <DisplayText type="bodyMedium" variant="regular" text="Profile" />
                </MenuItem>

                <MenuItem
                    onClick={handleLogOut}
                    sx={{
                        p: "8px 12px",
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                    }}
                >
                    <ExitToApp fontSize="small" />
                    <DisplayText type="bodyMedium" variant="regular" text="Log out" />
                </MenuItem>
            </AccountMenu>
        ),
        [userData, userActiveRole, getRoleLabel, handleProfileMenuOpen, handleProfileMenuClose, userRoles]
    );

    const drawerIcon = useMemo(() => {
        return (
            <IconButton onClick={toggleDrawer(true)} aria-label="open menu">
                <MenuIcon />
            </IconButton>
        );
    }, []);

    const hideTrialDropdown = useMemo(() => location.pathname.startsWith("/user-management"), [location.pathname]);

    const inebulaLogo = useMemo(() => {
        return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Link to="/">
                    <Image src={LogoImg} alt="VoxeleronLogo" />
                </Link>
                <Box>
                    <DisplayText
                        type="h5"
                        variant="regular"
                        text="iNebula"
                        style={{ marginBottom: "0px", fontSize: 28 }}
                    />
                    <DisplayText
                        type="bodyXSmall"
                        variant="regular"
                        text="by Voxeleron"
                        style={{ marginBottom: "0px", fontSize: 14, fontWeight: 200 }}
                    />
                </Box>
            </Box>
        );
    }, []);

    const rightMenuItems = useMemo(() => {
        return (
            <Box
                sx={{
                    display: "none",
                    [theme.breakpoints.up("sm")]: {
                        display: "flex",
                        gap: "12px",
                    },
                }}
            >
                <ComingSoonFeature>
                    <Box sx={{ position: "relative", top: "5px" }}>
                        <Badge badgeContent={5} color="secondary" sx={{ position: "relative", top: "5px" }}>
                            <NotificationsNoneOutlined sx={{ color: "#9aa7c6", fontSize: "28px" }} />
                        </Badge>
                    </Box>
                </ComingSoonFeature>
                <Button
                    aria-label="My Account"
                    aria-controls={menuId}
                    size="large"
                    sx={{ color: "#9aa7c6" }}
                    aria-haspopup="true"
                    startIcon={<AccountCircleOutlined sx={{ fontSize: "28px !important", marginRight: "8px" }} />}
                    onClick={handleProfileMenuOpen}
                >
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                        <DisplayText
                            style={{ color: "white" }}
                            text={userData?.email || ""}
                            type="bodyMedium"
                            variant="semiBold"
                        />
                        <DisplayText
                            style={{ color: "#9aa7c6", textTransform: "none", lineHeight: "8px" }}
                            text={
                                getRoleLabel(userActiveRole || "") === "Master admin"
                                    ? "System Admin"
                                    : getRoleLabel(userActiveRole || "")
                            }
                            type="bodySmall"
                            variant="semiBold"
                        />
                    </Box>
                </Button>
                {profileMenu}
            </Box>
        );
    }, [userData, userActiveRole, getRoleLabel, handleProfileMenuOpen]);

    return (
        <Box sx={{ flex: "0 0 74px", border: "1px solid #354051" }}>
            <AppBar sx={{ bgcolor: theme.palette.background.default, boxShadow: "none", padding: 0 }} position="static">
                <Drawer open={menuOpen} onClose={toggleDrawer(false)}>
                    <Menu onExportClick={handleExportClick} onClose={toggleDrawer(false)} />
                </Drawer>
                <Toolbar sx={{ backgroundColor: "#29323f", height: 72 }}>
                    {drawerIcon}
                    <Box
                        sx={{
                            marginLeft: 8,
                            marginRight: 16,
                            display: { xs: "none", md: "block" },
                        }}
                    >
                        {inebulaLogo}
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            flex: 1,
                            ml: 10,
                            alignItems: "center",
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 30 }}>
                            {!hideTrialDropdown && <TrialDropdown />}
                        </Box>

                        <Box>{rightMenuItems}</Box>
                    </Box>
                </Toolbar>
            </AppBar>
            {exportModalOpen && <ExportCustomFormModal open={exportModalOpen} onClose={handleClose} />}
        </Box>
    );
};
export default NavBar;
